import { can } from '@hrms/shared'
import { useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useCurrentUser } from '../../../auth/AuthProvider.tsx'

/** The drawer is URL-driven: `?ticket=HR-1001` on any HR desk page (deep-linkable). */
export const TICKET_PARAM = 'ticket'
const HR_BASE = '/hr'

export function useTicketParam(): string | null {
  const [params] = useSearchParams()
  return params.get(TICKET_PARAM)
}

/**
 * Opens the drawer on the current HR desk page; from anywhere else, on the caller's landing
 * page (Tickets for HR and viewers, My tickets for employees).
 */
export function useOpenTicket() {
  const navigate = useNavigate()
  const location = useLocation()
  const fallback = can(useCurrentUser(), 'queue.view') ? 'tickets' : 'my-tickets'
  return useCallback(
    (key: string) => {
      const onHrPage = location.pathname === HR_BASE || location.pathname.startsWith(`${HR_BASE}/`)
      const params = new URLSearchParams(onHrPage ? location.search : '')
      params.set(TICKET_PARAM, key)
      navigate({
        pathname: onHrPage ? location.pathname : `${HR_BASE}/${fallback}`,
        search: `?${params}`,
      })
    },
    [navigate, location.pathname, location.search, fallback],
  )
}

export function useCloseTicket() {
  const [, setParams] = useSearchParams()
  return useCallback(
    () =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p)
          next.delete(TICKET_PARAM)
          return next
        },
        { replace: true },
      ),
    [setParams],
  )
}
